import React, { useState, useEffect, useMemo } from "react";
import { toast } from 'components/Toast';
import { cn } from "utils";
import styles from "./reports.module.scss";
import TabList from "./TabList";
import { Outlet } from "react-router-dom";
import DownloadIcon from "assets/images/icon/download-icon";
import { useQuery } from "@tanstack/react-query";
import { getPayrolls, previewPayroll } from "services/payrolls";
import { getEmployees } from "services/employees";
import { getContractors } from "services/contractors";
import { getCompanyBenefits, getEmployeeBenefits } from "services/benefits";
import { getMiscDeductions, getChildSupportDeductions } from "services/deductions";
import { downloadW2Pdf, download1099Pdf, download941Pdf, downloadW2cPdf, publishW2s, publish1099s, publishW2cs, publishForm1099Misc } from "services/reports";
import { createNotification } from "services/timeOff";

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '';
const fmtMoney = (v) => v != null ? Number(v).toFixed(2) : '0.00';

const downloadCSV = (rows, filename) => {
    const csv = rows.map(r => r.map(cell => {
        const s = String(cell ?? '').replace(/"/g, '""');
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

const Reports = () => {
    const [downloading, setDownloading] = useState(null);
    const [pdfYear, setPdfYear] = useState(new Date().getFullYear() - 1);
    const [pdfQuarter, setPdfQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3) || 1);
    const [publishing, setPublishing] = useState(null);
    const [publishResult, setPublishResult] = useState(null);

    const { data: payrollsRaw } = useQuery({
        queryKey: ['payrolls'],
        queryFn: getPayrolls,
        staleTime: 5 * 60 * 1000,
    });

    const approvedPayrolls = useMemo(() => {
        const list = Array.isArray(payrollsRaw) ? payrollsRaw : (payrollsRaw?.data ?? []);
        return list.filter(p => p.status === 'approved');
    }, [payrollsRaw]);

    // Always show current year and previous year — updates automatically each January
    const currentYear = new Date().getFullYear();
    const availablePdfYears = useMemo(() => [currentYear, currentYear - 1], [currentYear]);

    // Snap pdfYear to the most recent available year on first load
    useEffect(() => {
        if (!availablePdfYears.includes(pdfYear)) {
            setPdfYear(availablePdfYears[0]);
        }
    }, [availablePdfYears]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleEmployeeJournal = async () => {
        setDownloading('emp');
        try {
            const rows = [['Pay Period', 'Pay Date', 'Type', 'Gross Pay', 'Total Taxes', 'Net Pay']];
            for (const p of approvedPayrolls) {
                try {
                    const calc = await previewPayroll(p.id);
                    const employees = calc?.employees ?? [];
                    if (employees.length === 0) continue;
                    employees.forEach(emp => {
                        rows.push([
                            p.startDate && p.endDate ? `${fmt(p.startDate)} - ${fmt(p.endDate)}` : fmt(p.payDate),
                            fmt(p.payDate),
                            p.type || 'regular',
                            `${emp.employeeFirstName} ${emp.employeeLastName}`,
                            emp.payType || '',
                            fmtMoney(emp.grossPay),
                            fmtMoney(emp.federalIncomeTax),
                            fmtMoney(emp.stateIncomeTax),
                            fmtMoney(emp.socialSecurityTax),
                            fmtMoney(emp.medicareTax),
                            fmtMoney(emp.preTaxDeductions),
                            fmtMoney(emp.postTaxDeductions),
                            fmtMoney(emp.netPay),
                            emp.paymentMethod || '',
                        ]);
                    });
                } catch (_) {}
            }
            rows[0] = ['Pay Period', 'Pay Date', 'Type', 'Employee', 'Pay Type', 'Gross Pay', 'Federal Tax', 'State Tax', 'Social Security', 'Medicare', 'Pre-Tax Ded.', 'Post-Tax Ded.', 'Net Pay', 'Payment Method'];
            downloadCSV(rows, `employee_payroll_journal_${new Date().toISOString().slice(0,10)}.csv`);
        } finally {
            setDownloading(null);
        }
    };

    const handleContractorJournal = async () => {
        setDownloading('con');
        try {
            const rows = [['Pay Period', 'Pay Date', 'Type', 'Contractor', 'Business Name', 'Hours', 'Pay Rate', 'Reimbursement', 'Gross Pay', 'Payment Method']];
            for (const p of approvedPayrolls) {
                try {
                    const calc = await previewPayroll(p.id);
                    const contractors = calc?.contractors ?? [];
                    if (contractors.length === 0) continue;
                    contractors.forEach(c => {
                        rows.push([
                            p.startDate && p.endDate ? `${fmt(p.startDate)} - ${fmt(p.endDate)}` : fmt(p.payDate),
                            fmt(p.payDate),
                            p.type || 'regular',
                            `${c.firstName} ${c.lastName}`,
                            c.businessName || '',
                            fmtMoney(c.hours),
                            fmtMoney(c.payRate),
                            fmtMoney(c.reimbursement),
                            fmtMoney(c.grossPay),
                            c.paymentMethod || '',
                        ]);
                    });
                } catch (_) {}
            }
            downloadCSV(rows, `contractor_payroll_journal_${new Date().toISOString().slice(0,10)}.csv`);
        } finally {
            setDownloading(null);
        }
    };

    const handleW2Preview = async () => {
        setDownloading('w2');
        try {
            const ytd = {};
            for (const p of approvedPayrolls) {
                try {
                    const calc = await previewPayroll(p.id);
                    (calc?.employees ?? []).forEach(emp => {
                        const key = emp.employeeId;
                        if (!ytd[key]) ytd[key] = { name: `${emp.employeeFirstName} ${emp.employeeLastName}`, payType: emp.payType, grossPay: 0, federalTax: 0, stateTax: 0, ss: 0, medicare: 0, preTax: 0, postTax: 0, netPay: 0 };
                        ytd[key].grossPay    += Number(emp.grossPay || 0);
                        ytd[key].federalTax  += Number(emp.federalIncomeTax || 0);
                        ytd[key].stateTax    += Number(emp.stateIncomeTax || 0);
                        ytd[key].ss          += Number(emp.socialSecurityTax || 0);
                        ytd[key].medicare    += Number(emp.medicareTax || 0);
                        ytd[key].preTax      += Number(emp.preTaxDeductions || 0);
                        ytd[key].postTax     += Number(emp.postTaxDeductions || 0);
                        ytd[key].netPay      += Number(emp.netPay || 0);
                    });
                } catch (_) {}
            }
            const rows = [['Employee', 'Pay Type', 'YTD Gross Pay', 'Federal Tax', 'State Tax', 'Social Security', 'Medicare', 'Pre-Tax Ded.', 'Post-Tax Ded.', 'YTD Net Pay']];
            Object.values(ytd).forEach(e => {
                rows.push([e.name, e.payType, fmtMoney(e.grossPay), fmtMoney(e.federalTax), fmtMoney(e.stateTax), fmtMoney(e.ss), fmtMoney(e.medicare), fmtMoney(e.preTax), fmtMoney(e.postTax), fmtMoney(e.netPay)]);
            });
            downloadCSV(rows, `w2_preview_${new Date().getFullYear()}.csv`);
        } finally {
            setDownloading(null);
        }
    };

    const handleW2Exempt = async () => {
        setDownloading('exempt');
        try {
            const rows = [['Employee', 'Pay Type', 'YTD Gross Pay', 'YTD Net Pay', 'Federal Tax Withheld', 'Note']];
            for (const p of approvedPayrolls) {
                try {
                    const calc = await previewPayroll(p.id);
                    (calc?.employees ?? []).forEach(emp => {
                        if (Number(emp.federalIncomeTax || 0) === 0 && Number(emp.grossPay || 0) > 0) {
                            rows.push([
                                `${emp.employeeFirstName} ${emp.employeeLastName}`,
                                emp.payType || '',
                                fmtMoney(emp.grossPay),
                                fmtMoney(emp.netPay),
                                '0.00',
                                'Exempt or $0 FITW',
                            ]);
                        }
                    });
                } catch (_) {}
            }
            downloadCSV(rows, `w2_exempt_${new Date().getFullYear()}.csv`);
        } finally {
            setDownloading(null);
        }
    };


    // ---- NEW ANALYTICAL REPORTS ----

    const handleTaxLiabilityReport = async () => {
        setDownloading('taxliability');
        try {
            const rows = [['Pay Period', 'Pay Date', 'Federal Income Tax', 'State Income Tax', 'Social Security (EE)', 'Medicare (EE)', 'Total Employee Taxes', 'Approx. Employer SS', 'Approx. Employer Medicare', 'Total Tax Liability']];
            for (const p of approvedPayrolls) {
                try {
                    const calc = await previewPayroll(p.id);
                    const employees = calc?.employees ?? [];
                    if (!employees.length) continue;
                    const period = p.startDate && p.endDate ? `${fmt(p.startDate)} - ${fmt(p.endDate)}` : fmt(p.payDate);
                    const fed   = employees.reduce((s, e) => s + Number(e.federalIncomeTax || 0), 0);
                    const state = employees.reduce((s, e) => s + Number(e.stateIncomeTax || 0), 0);
                    const ss    = employees.reduce((s, e) => s + Number(e.socialSecurityTax || 0), 0);
                    const med   = employees.reduce((s, e) => s + Number(e.medicareTax || 0), 0);
                    const empTotal = fed + state + ss + med;
                    const erSS  = ss;  // employer SS matches employee SS
                    const erMed = med; // employer Medicare matches employee Medicare
                    const total = empTotal + erSS + erMed;
                    rows.push([period, fmt(p.payDate), fmtMoney(fed), fmtMoney(state), fmtMoney(ss), fmtMoney(med), fmtMoney(empTotal), fmtMoney(erSS), fmtMoney(erMed), fmtMoney(total)]);
                } catch (_) {}
            }
            downloadCSV(rows, `tax_liability_${new Date().toISOString().slice(0,10)}.csv`);
        } finally {
            setDownloading(null);
        }
    };

    const handleContractor1099Summary = async () => {
        setDownloading('1099');
        try {
            const ytd = {};
            for (const p of approvedPayrolls) {
                try {
                    const calc = await previewPayroll(p.id);
                    (calc?.contractors ?? []).forEach(c => {
                        const key = c.contractorId || c.id;
                        if (!ytd[key]) ytd[key] = { name: `${c.firstName || ''} ${c.lastName || ''}`.trim(), business: c.businessName || '', grossPay: 0, reimbursement: 0, payments: 0 };
                        ytd[key].grossPay     += Number(c.grossPay || 0);
                        ytd[key].reimbursement += Number(c.reimbursement || 0);
                        ytd[key].payments++;
                    });
                } catch (_) {}
            }
            const rows = [['Contractor', 'Business Name', 'YTD Gross Pay', 'YTD Reimbursements', 'Total YTD Amount', 'Payment Count', '1099-NEC Required']];
            Object.values(ytd).forEach(c => {
                const total = c.grossPay + c.reimbursement;
                rows.push([c.name, c.business, fmtMoney(c.grossPay), fmtMoney(c.reimbursement), fmtMoney(total), c.payments, total >= 600 ? 'Yes' : 'No (< $600)']);
            });
            downloadCSV(rows, `contractor_1099_summary_${new Date().getFullYear()}.csv`);
        } finally {
            setDownloading(null);
        }
    };

    const handleHeadcountReport = async () => {
        setDownloading('headcount');
        try {
            const [employees, contractors] = await Promise.all([getEmployees(), getContractors()]);
            const rows = [['Type', 'Name', 'Status', 'Pay Type', 'Start Date', 'Department/Workplace']];
            employees.forEach(e => {
                rows.push(['Employee', `${e.firstName || ''} ${e.lastName || ''}`.trim(), e.active !== false ? 'Active' : 'Inactive', e.payType || '', e.startDate ? fmt(e.startDate) : '', e.departmentId || e.department || '']);
            });
            contractors.forEach(c => {
                rows.push(['Contractor', `${c.firstName || ''} ${c.lastName || ''}`.trim(), c.active !== false ? 'Active' : 'Inactive', c.payType || '', c.startDate ? fmt(c.startDate) : '', c.departmentId || '']);
            });
            const summary = [
                [],
                ['SUMMARY'],
                ['Total Employees', employees.length],
                ['Active Employees', employees.filter(e => e.active !== false).length],
                ['Total Contractors', contractors.length],
                ['Active Contractors', contractors.filter(c => c.active !== false).length],
                ['Total Workforce', employees.length + contractors.length],
            ];
            downloadCSV([...rows, ...summary], `headcount_${new Date().toISOString().slice(0,10)}.csv`);
        } finally {
            setDownloading(null);
        }
    };

    const handleBenefitsCostSummary = async () => {
        setDownloading('benefits');
        try {
            const [companyBenefits, employeeBenefits] = await Promise.all([getCompanyBenefits(), getEmployeeBenefits()]);
            const rows = [['Benefit Plan', 'Type', 'Company Contribution (per enrollee)', 'Employee Deduction (per enrollee)', 'Enrolled Count', 'Total Monthly Company Cost', 'Total Monthly Employee Cost']];
            companyBenefits.forEach(plan => {
                const enrolled = employeeBenefits.filter(eb =>
                    eb.companyBenefitId === plan.id || eb.benefitId === plan.id
                );
                const count = enrolled.length;
                const totalCompany = enrolled.reduce((s, eb) => s + Number(eb.companyContribution || plan.companyContribution || 0), 0);
                const totalEmployee = enrolled.reduce((s, eb) => s + Number(eb.employeeDeduction || plan.employeeDeduction || 0), 0);
                rows.push([
                    plan.name || plan.benefitName || plan.title || 'Benefit',
                    plan.type || plan.benefitType || '—',
                    fmtMoney(plan.companyContribution),
                    fmtMoney(plan.employeeDeduction),
                    count,
                    fmtMoney(totalCompany),
                    fmtMoney(totalEmployee),
                ]);
            });
            downloadCSV(rows, `benefits_cost_summary_${new Date().toISOString().slice(0,10)}.csv`);
        } finally {
            setDownloading(null);
        }
    };

    const handleDeductionsSummary = async () => {
        setDownloading('deductions');
        try {
            const [miscDeductions, childSupportDeductions] = await Promise.all([getMiscDeductions(), getChildSupportDeductions()]);
            const rows = [['Employee ID', 'Type', 'Amount', 'Description']];
            miscDeductions.forEach(d => {
                rows.push([d.employeeId || '—', 'Miscellaneous', fmtMoney(d.amount), d.description || '']);
            });
            childSupportDeductions.forEach(d => {
                rows.push([d.employeeId || '—', 'Child Support', fmtMoney(d.amount), d.description || d.caseNumber || '']);
            });
            const totalMisc = miscDeductions.reduce((s, d) => s + Number(d.amount || 0), 0);
            const totalChild = childSupportDeductions.reduce((s, d) => s + Number(d.amount || 0), 0);
            rows.push([], ['SUMMARY'], ['Total Misc Deductions', fmtMoney(totalMisc)], ['Total Child Support', fmtMoney(totalChild)], ['Grand Total', fmtMoney(totalMisc + totalChild)]);
            downloadCSV(rows, `deductions_summary_${new Date().toISOString().slice(0,10)}.csv`);
        } finally {
            setDownloading(null);
        }
    };

    const handleBenefitEnrollmentExport = async () => {
        setDownloading('benefitenroll');
        try {
            const [employees, companyBenefits, employeeBenefits] = await Promise.all([
                getEmployees(), getCompanyBenefits(), getEmployeeBenefits()
            ]);
            const empMap = {};
            employees.forEach(e => { empMap[e.id] = `${e.firstName || ''} ${e.lastName || ''}`.trim(); });
            const planMap = {};
            companyBenefits.forEach(p => { planMap[p.id] = p; });

            const rows = [[
                'Employee Name', 'Employee ID', 'Benefit Plan', 'Type', 'Period',
                'Effective Start', 'Effective End', 'Employee Contribution ($)',
                'Employer Contribution ($)', 'Total Premium ($)', 'Pre-Tax', 'Status'
            ]];
            employeeBenefits.forEach(eb => {
                const plan = planMap[eb.companyBenefitId] || {};
                rows.push([
                    empMap[eb.employeeId] || eb.employeeId || '—',
                    eb.employeeId || '—',
                    plan.description || plan.name || eb.description || '—',
                    eb.type || plan.type || '—',
                    eb.period || plan.period || '—',
                    fmt(eb.effectiveStart || eb.effective),
                    fmt(eb.effectiveEnd) || 'Ongoing',
                    fmtMoney(eb.employeeDeduction || eb.employeeContributionAmount),
                    fmtMoney(eb.companyContribution || eb.companyContributionAmount),
                    fmtMoney(eb.totalPremiumAmount || plan.totalPremiumAmount),
                    (eb.preTax ?? plan.preTax) ? 'Yes' : 'No',
                    eb.active === false ? 'Inactive' : 'Active',
                ]);
            });
            const today = new Date().toISOString().slice(0, 10);
            downloadCSV(rows, `benefit_enrollment_export_${today}.csv`);
        } finally { setDownloading(null); }
    };

    const handle401kContributionExport = async () => {
        setDownloading('401kexport');
        try {
            const [employees, employeeBenefits] = await Promise.all([getEmployees(), getEmployeeBenefits()]);
            const empMap = {};
            employees.forEach(e => { empMap[e.id] = e; });

            const enrolled401k = employeeBenefits.filter(eb =>
                (eb.type || '').toLowerCase().includes('401k') ||
                (eb.type || '').toLowerCase().includes('401(k)') ||
                (eb.type || '').toLowerCase().includes('retirement')
            );

            const rows = [[
                'Employee Name', 'Employee ID', 'Pay Date', 'Pay Period Start', 'Pay Period End',
                'Gross Pay ($)', 'Employee 401k Contribution ($)', 'Employer Match ($)',
                'Employee Contribution %', 'Employer Match %', 'Status'
            ]];

            for (const p of approvedPayrolls) {
                try {
                    const calc = await previewPayroll(p.id);
                    (calc?.employees ?? []).forEach(emp => {
                        const eb = enrolled401k.find(e => e.employeeId === (emp.employeeId || emp.id));
                        if (!eb) return;
                        const gross = Number(emp.grossPay || 0);
                        const empPct = Number(eb.employeeContributionPercent || 0);
                        const empAmt = Number(eb.employeeDeduction || eb.employeeContributionAmount || 0) || (gross * empPct / 100);
                        const erPct = Number(eb.companyContributionPercent || 0);
                        const erAmt = Number(eb.companyContribution || eb.companyContributionAmount || 0) || (gross * erPct / 100);
                        rows.push([
                            `${emp.employeeFirstName || ''} ${emp.employeeLastName || ''}`.trim(),
                            emp.employeeId || emp.id || '—',
                            fmt(p.payDate || p.endDate),
                            fmt(p.startDate),
                            fmt(p.endDate),
                            fmtMoney(gross),
                            fmtMoney(empAmt),
                            fmtMoney(erAmt),
                            empPct ? `${empPct}%` : '—',
                            erPct ? `${erPct}%` : '—',
                            eb.active === false ? 'Inactive' : 'Active',
                        ]);
                    });
                } catch (_) {}
            }

            if (rows.length === 1) {
                toast('No 401k-enrolled employees found in approved payrolls. Make sure employees have a 401k benefit assigned.', 'warning');
                return;
            }
            const today = new Date().toISOString().slice(0, 10);
            downloadCSV(rows, `401k_contribution_export_${today}.csv`);
        } finally { setDownloading(null); }
    };

    const handleHsaFsaExport = async () => {
        setDownloading('hsafsa');
        try {
            const [employees, employeeBenefits, companyBenefits] = await Promise.all([
                getEmployees(), getEmployeeBenefits(), getCompanyBenefits()
            ]);
            const empMap = {};
            employees.forEach(e => { empMap[e.id] = e; });
            const planMap = {};
            companyBenefits.forEach(p => { planMap[p.id] = p; });

            const hsaFsaTypes = ['hsa', 'fsa', 'healthfsa', 'dependentfsa', 'health fsa', 'dependent fsa', 'dependent care'];
            const enrolled = employeeBenefits.filter(eb => {
                const t = (eb.type || planMap[eb.companyBenefitId]?.type || '').toLowerCase();
                return hsaFsaTypes.some(x => t.includes(x));
            });

            if (!enrolled.length) {
                toast('No HSA or FSA benefits found. Make sure employees are enrolled in an HSA or FSA benefit plan.', 'warning');
                return;
            }

            const rows = [[
                'Employee Name', 'Employee ID', 'Account Type', 'Pay Date',
                'Pay Period Start', 'Pay Period End', 'Gross Pay ($)',
                'Employee HSA/FSA Contribution ($)', 'Employer Contribution ($)',
                'Annual Limit ($)', 'YTD Employee Contribution ($)', 'Status'
            ]];

            // IRS 2024 HSA limits: $4,150 individual / $8,300 family
            const HSA_ANNUAL_LIMIT = 4150;
            const FSA_ANNUAL_LIMIT = 3200;

            const ytdMap = {};
            for (const p of approvedPayrolls) {
                try {
                    const calc = await previewPayroll(p.id);
                    (calc?.employees ?? []).forEach(emp => {
                        const empId = emp.employeeId || emp.id;
                        const empEnrolled = enrolled.filter(e => e.employeeId === empId);
                        if (!empEnrolled.length) return;
                        const gross = Number(emp.grossPay || 0);
                        empEnrolled.forEach(eb => {
                            const plan = planMap[eb.companyBenefitId] || {};
                            const accountType = eb.type || plan.type || '—';
                            const empAmt = Number(eb.employeeDeduction || eb.employeeContributionAmount || eb.amount || 0);
                            const erAmt = Number(eb.companyContribution || eb.companyContributionAmount || plan.companyContributionAmount || 0);
                            const annualLimit = eb.hsaContributionLimit
                                || plan.hsaContributionLimit
                                || plan.annualLimit
                                || plan.limitAmount
                                || (accountType.toLowerCase().includes('hsa') ? HSA_ANNUAL_LIMIT : FSA_ANNUAL_LIMIT);
                            const key = `${empId}|${eb.id || eb.companyBenefitId}`;
                            ytdMap[key] = (ytdMap[key] || 0) + empAmt;
                            rows.push([
                                `${emp.employeeFirstName || ''} ${emp.employeeLastName || ''}`.trim(),
                                empId,
                                accountType,
                                fmt(p.payDate || p.endDate),
                                fmt(p.startDate),
                                fmt(p.endDate),
                                fmtMoney(gross),
                                fmtMoney(empAmt),
                                fmtMoney(erAmt),
                                fmtMoney(annualLimit),
                                fmtMoney(ytdMap[key]),
                                eb.active === false ? 'Inactive' : 'Active',
                            ]);
                        });
                    });
                } catch (_) {}
            }
            downloadCSV(rows, `hsa_fsa_contribution_export_${new Date().toISOString().slice(0,10)}.csv`);
        } finally { setDownloading(null); }
    };

    const handleCobraEligibilityReport = async () => {
        setDownloading('cobra');
        try {
            const [employees, employeeBenefits, companyBenefits] = await Promise.all([
                getEmployees(), getEmployeeBenefits(), getCompanyBenefits()
            ]);
            const planMap = {};
            companyBenefits.forEach(p => { planMap[p.id] = p; });

            // Employees terminated in the last 18 months (COBRA window)
            const cutoff = new Date();
            cutoff.setMonth(cutoff.getMonth() - 18);
            const terminated = employees.filter(e =>
                e.terminationDate && new Date(e.terminationDate) >= cutoff
            );

            if (!terminated.length) {
                toast('No terminated employees found in the last 18 months.', 'warning');
                return;
            }

            const rows = [[
                'Employee Name', 'Employee ID', 'Termination Date', 'Days Since Termination',
                'COBRA Deadline (60 days)', 'Benefit Plan', 'Benefit Type', 'Coverage Period',
                'Monthly Employee Premium ($)', 'Monthly Employer Cost ($)',
                'COBRA Admin Cost (102%) ($)', 'Pre-Tax', 'COBRA Status'
            ]];

            terminated.forEach(emp => {
                const termDate = new Date(emp.terminationDate);
                const today = new Date();
                const daysSince = Math.floor((today - termDate) / (1000 * 60 * 60 * 24));
                const deadlineDate = new Date(termDate);
                deadlineDate.setDate(deadlineDate.getDate() + 60);
                const cobraStatus = daysSince > 60 ? 'Deadline Passed' : daysSince > 45 ? 'URGENT – Deadline Soon' : 'Within Window';

                const empBenefits = employeeBenefits.filter(eb => eb.employeeId === emp.id && eb.active !== false);
                const qualifyingTypes = ['medical', 'dental', 'vision', 'hsa', 'fsa'];

                const cobraBenefits = empBenefits.filter(eb => {
                    const t = (eb.type || planMap[eb.companyBenefitId]?.type || '').toLowerCase();
                    return qualifyingTypes.some(x => t.includes(x));
                });

                if (!cobraBenefits.length) {
                    rows.push([
                        `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
                        emp.id, fmt(emp.terminationDate), daysSince,
                        fmt(deadlineDate.toISOString()), 'No qualifying benefits', '—', '—',
                        '0.00', '0.00', '0.00', '—', cobraStatus
                    ]);
                    return;
                }

                cobraBenefits.forEach(eb => {
                    const plan = planMap[eb.companyBenefitId] || {};
                    const empPremium = Number(eb.employeeDeduction || eb.employeeContributionAmount || 0);
                    const erCost = Number(eb.companyContribution || eb.companyContributionAmount || 0);
                    const totalPremium = empPremium + erCost;
                    const cobraAdminCost = totalPremium * 1.02;
                    rows.push([
                        `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
                        emp.id,
                        fmt(emp.terminationDate),
                        daysSince,
                        fmt(deadlineDate.toISOString()),
                        plan.description || plan.name || eb.description || '—',
                        eb.type || plan.type || '—',
                        eb.period || plan.period || '—',
                        fmtMoney(empPremium),
                        fmtMoney(erCost),
                        fmtMoney(cobraAdminCost),
                        (eb.preTax ?? plan.preTax) ? 'Yes' : 'No',
                        cobraStatus,
                    ]);
                });
            });

            rows.push([], ['NOTE: COBRA election notices must be sent within 14 days of the plan administrator being notified of a qualifying event.']);
            rows.push(['COBRA coverage can last up to 18 months (29 months for disability). Premium = employee + employer share + 2% admin fee.']);
            downloadCSV(rows, `cobra_eligibility_report_${new Date().toISOString().slice(0,10)}.csv`);
        } finally { setDownloading(null); }
    };

    const handleEdi834Export = async () => {
        setDownloading('edi834');
        try {
            const [employees, employeeBenefits, companyBenefits] = await Promise.all([
                getEmployees(), getEmployeeBenefits(), getCompanyBenefits()
            ]);
            const empMap = {};
            employees.forEach(e => { empMap[e.id] = e; });
            const planMap = {};
            companyBenefits.forEach(p => { planMap[p.id] = p; });

            const now = new Date();
            const dt = now.toISOString().slice(0,10).replace(/-/g,'');
            const tm = now.toTimeString().slice(0,5).replace(':','');

            // Map benefit types to official X12 HD03 Insurance Line Codes
            const toLineCode = (type) => {
                const t = (type || '').toLowerCase();
                if (t.includes('medical') || t.includes('health'))  return 'HLT';
                if (t.includes('dental'))                            return 'DEN';
                if (t.includes('vision'))                            return 'VIS';
                if (t.includes('hsa'))                               return 'HLT'; // paired with HDHP
                if (t.includes('fsa'))                               return 'HLT';
                if (t.includes('ltd') || t.includes('long'))        return 'LTD';
                if (t.includes('std') || t.includes('short'))       return 'STD';
                if (t.includes('life') || t.includes('insurance'))  return 'LIF';
                if (t.includes('401') || t.includes('retire'))      return 'RET';
                return 'HLT';
            };

            // Build EDI 834 (ANSI X12 5010 834) — one INS loop per employee, multiple HD loops
            const segments = [];
            const seg = (id, ...els) => { const s = [id, ...els].join('*') + '~'; segments.push(s); };

            const companyName = 'PAYROLLNOOT EMPLOYER';
            const ein = '000000000';

            // Interchange / group envelope
            seg('ISA','00','          ','00','          ','ZZ','SENDER         ','ZZ','RECEIVER       ',dt.slice(2),tm,'^','00501','000000001','0','P',':');
            seg('GS','BE','SENDER','RECEIVER',dt,tm,'1','X','005010X220A1');
            seg('ST','834','0001','005010X220A1');
            seg('BGN','00','REF001',dt,tm,'','','2');
            seg('REF','38',ein);
            seg('DTP','007','D8',dt);
            seg('N1','P5',companyName,'XX',ein);

            // Group benefits by employee (one INS loop per person)
            const benefitsByEmp = {};
            employeeBenefits.forEach(eb => {
                if (!empMap[eb.employeeId]) return;
                if (!benefitsByEmp[eb.employeeId]) benefitsByEmp[eb.employeeId] = [];
                benefitsByEmp[eb.employeeId].push(eb);
            });

            Object.entries(benefitsByEmp).forEach(([empId, ebs]) => {
                const emp = empMap[empId];
                // Use the most-recent effective date as the member effective date
                const firstEb = ebs[0];
                const memberMaint = ebs.every(e => e.active === false) ? '024' : '021';
                const effDate = (firstEb.effectiveStart || firstEb.effective || dt).replace(/-/g,'').slice(0,8).padEnd(8,'0');
                const dob = (emp.dateOfBirth || '').replace(/-/g,'').slice(0,8) || '19800101';
                const gender = emp.gender === 'Female' ? 'F' : 'M';

                seg('INS','Y','18',memberMaint,'XN','A','C','FT');
                seg('REF','0F',empId);
                seg('DTP','356','D8',effDate);
                seg('NM1','IL','1',emp.lastName || '',emp.firstName || '','','','','34',emp.ssn || '');
                seg('N3',emp.address || emp.addressLine1 || '');
                seg('N4',emp.city || '',emp.state || '',emp.zip || emp.postalCode || '');
                seg('DMG','D8',dob,gender);

                // One HD loop per benefit plan under this member
                ebs.forEach(eb => {
                    const plan = planMap[eb.companyBenefitId] || {};
                    const lineCode = toLineCode(eb.type || plan.type || '');
                    const hdMaint = eb.active === false ? '024' : '021';
                    const hdEff = (eb.effectiveStart || eb.effective || dt).replace(/-/g,'').slice(0,8).padEnd(8,'0');
                    seg('HD',hdMaint,'',lineCode,plan.description || plan.name || '','EMP');
                    seg('DTP','348','D8',hdEff);
                    if (eb.active === false && emp.terminationDate) {
                        seg('DTP','349','D8',emp.terminationDate.replace(/-/g,'').slice(0,8));
                    }
                });
            });

            // SE counts segments from ST to SE inclusive (segments array currently excludes SE/GE/IEA)
            const stIdx = segments.findIndex(s => s.startsWith('ST*'));
            const segCount = segments.length - stIdx + 1; // +1 to include SE itself
            seg('SE',String(segCount),'0001');
            seg('GE','1','1');
            seg('IEA','1','000000001');

            const edi = segments.join('\n') + '\n';
            const blob = new Blob([edi], { type: 'text/plain;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `edi834_enrollment_${dt}.txt`;
            a.click();
            URL.revokeObjectURL(url);
        } finally { setDownloading(null); }
    };

    const handleLaborCostReport = async () => {
        setDownloading('laborcost');
        try {
            const ytd = {};
            for (const p of approvedPayrolls) {
                try {
                    const calc = await previewPayroll(p.id);
                    (calc?.employees ?? []).forEach(emp => {
                        const key = emp.employeeId || emp.id;
                        if (!ytd[key]) ytd[key] = { name: `${emp.employeeFirstName || ''} ${emp.employeeLastName || ''}`.trim(), payType: emp.payType || '', grossPay: 0, taxes: 0, deductions: 0, netPay: 0, periods: 0 };
                        ytd[key].grossPay   += Number(emp.grossPay || 0);
                        ytd[key].taxes      += Number(emp.federalIncomeTax || 0) + Number(emp.stateIncomeTax || 0) + Number(emp.socialSecurityTax || 0) + Number(emp.medicareTax || 0);
                        ytd[key].deductions += Number(emp.preTaxDeductions || 0) + Number(emp.postTaxDeductions || 0);
                        ytd[key].netPay     += Number(emp.netPay || 0);
                        ytd[key].periods++;
                    });
                } catch (_) {}
            }
            const rows = [['Employee', 'Pay Type', 'YTD Gross Pay', 'YTD Taxes', 'YTD Deductions', 'YTD Net Pay', 'Employer Payroll Tax (est.)', 'Total Employer Labor Cost', 'Pay Periods']];
            Object.values(ytd).forEach(e => {
                const erTax  = e.taxes * 0.5; // SS + Medicare matched by employer (approx equal to employee portion)
                const laborCost = e.grossPay + erTax;
                rows.push([e.name, e.payType, fmtMoney(e.grossPay), fmtMoney(e.taxes), fmtMoney(e.deductions), fmtMoney(e.netPay), fmtMoney(erTax), fmtMoney(laborCost), e.periods]);
            });
            const totalGross = Object.values(ytd).reduce((s, e) => s + e.grossPay, 0);
            rows.push([], ['TOTAL', '', fmtMoney(totalGross)]);
            downloadCSV(rows, `labor_cost_${new Date().toISOString().slice(0,10)}.csv`);
        } finally {
            setDownloading(null);
        }
    };

    const handleW2Pdf = async () => {
        setDownloading('w2pdf');
        try { await downloadW2Pdf(pdfYear); }
        catch (e) { toast(e?.response?.data || e?.message || 'W-2 PDF download failed. Check that payrolls have been approved for this year.', 'error'); }
        finally { setDownloading(null); }
    };

    const handle1099Pdf = async () => {
        setDownloading('1099pdf');
        try { await download1099Pdf(pdfYear); }
        catch (e) { toast(e?.response?.data || e?.message || '1099-NEC PDF download failed. Check that contractors with $600+ exist for this year.', 'error'); }
        finally { setDownloading(null); }
    };

    const handle941Pdf = async () => {
        setDownloading('941pdf');
        try { await download941Pdf(pdfYear, pdfQuarter); }
        catch (e) { toast(e?.response?.data || e?.message || 'Form 941 PDF download failed.', 'error'); }
        finally { setDownloading(null); }
    };

    const handlePublishW2s = async () => {
        setPublishing('w2');
        setPublishResult(null);
        try {
            const result = await publishW2s(pdfYear);
            for (const r of (result.recipients || [])) {
                try {
                    await createNotification({
                        recipientId: r.recipientId,
                        recipientType: 'employee',
                        type: 'document_uploaded',
                        title: `Your W-2 for ${pdfYear} is ready`,
                        message: `Your W-2 Wage and Tax Statement for ${pdfYear} is now available in your Employee Portal.`,
                        link: '/employee/documents',
                    });
                } catch (_) {}
            }
            setPublishResult({ type: 'w2', count: result.published, year: result.year });
            toast(`W-2s published to ${result.published} employee${result.published !== 1 ? 's' : ''} for ${result.year}.`, 'success');
        } catch (e) {
            toast(e?.response?.data?.message || e?.response?.data || e?.message || 'W-2 publish failed.', 'error');
        } finally {
            setPublishing(null);
        }
    };

    const handlePublish1099s = async () => {
        setPublishing('1099');
        setPublishResult(null);
        try {
            const result = await publish1099s(pdfYear);
            for (const r of (result.recipients || [])) {
                try {
                    await createNotification({
                        recipientId: r.recipientId,
                        recipientType: 'contractor',
                        type: 'document_uploaded',
                        title: `Your 1099-NEC for ${pdfYear} is ready`,
                        message: `Your 1099-NEC for ${pdfYear} is now available in your Contractor Portal.`,
                        link: '/contractor-portal/documents',
                    });
                } catch (_) {}
            }
            setPublishResult({ type: '1099', count: result.published, year: result.year });
            toast(`1099-NECs published to ${result.published} contractor${result.published !== 1 ? 's' : ''} for ${result.year}.`, 'success');
        } catch (e) {
            toast(e?.response?.data?.message || e?.response?.data || e?.message || '1099 publish failed.', 'error');
        } finally {
            setPublishing(null);
        }
    };

    // ── W-2c ────────────────────────────────────────────────────────────────
    const handlePublishW2cs = async () => {
        setPublishing('w2c');
        setPublishResult(null);
        try {
            const result = await publishW2cs(pdfYear);
            for (const r of (result.recipients || [])) {
                try {
                    await createNotification({
                        recipientId: r.recipientId,
                        recipientType: 'employee',
                        type: 'document_uploaded',
                        title: `Your Corrected W-2c for ${pdfYear} is ready`,
                        message: `A corrected W-2c for ${pdfYear} has been issued. Please use the corrected amounts when filing your return.`,
                        link: '/employee/documents',
                    });
                } catch (_) {}
            }
            setPublishResult({ type: 'w2c', count: result.published, year: result.year });
        } catch (e) {
            toast(e?.response?.data?.message || e?.response?.data || e?.message || 'W-2c publish failed.', 'error');
        } finally {
            setPublishing(null);
        }
    };

    const handleW2cPdf = async () => {
        setDownloading('w2cpdf');
        try { await downloadW2cPdf(pdfYear); }
        catch (e) { toast(e?.response?.data || e?.message || 'W-2c PDF download failed.', 'error'); }
        finally { setDownloading(null); }
    };

    // ── 1099-MISC ────────────────────────────────────────────────────────────
    const emptyMiscRecipient = { name: '', businessName: '', taxId: '', addressLine1: '', city: '', state: '', zip: '', box1Rents: '', box2Royalties: '', box3OtherIncome: '', box4FederalTaxWithheld: '', box6MedicalPayments: '', box8SubstitutePayments: '', box10CropInsurance: '', box14AttorneyFees: '' };
    const [miscRecipients, setMiscRecipients] = React.useState([{ ...emptyMiscRecipient }]);
    const [showMiscForm, setShowMiscForm] = React.useState(false);

    const updateMiscRecipient = (i, field, value) =>
        setMiscRecipients(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

    const handlePublish1099Misc = async () => {
        const payload = miscRecipients
            .filter(r => r.name.trim())
            .map(r => ({
                name: r.name, businessName: r.businessName, taxId: r.taxId,
                addressLine1: r.addressLine1, city: r.city, state: r.state, zip: r.zip,
                box1Rents: parseFloat(r.box1Rents) || 0,
                box2Royalties: parseFloat(r.box2Royalties) || 0,
                box3OtherIncome: parseFloat(r.box3OtherIncome) || 0,
                box4FederalTaxWithheld: parseFloat(r.box4FederalTaxWithheld) || 0,
                box6MedicalPayments: parseFloat(r.box6MedicalPayments) || 0,
                box8SubstitutePayments: parseFloat(r.box8SubstitutePayments) || 0,
                box10CropInsurance: parseFloat(r.box10CropInsurance) || 0,
                box14AttorneyFees: parseFloat(r.box14AttorneyFees) || 0,
            }));
        if (!payload.length) { toast('Add at least one recipient with a name.', 'warning'); return; }
        setPublishing('1099misc');
        try {
            const result = await publishForm1099Misc(pdfYear, payload);
            setPublishResult({ type: '1099misc', count: result.published, year: result.year });
            setMiscRecipients([{ ...emptyMiscRecipient }]);
            setShowMiscForm(false);
        } catch (e) {
            toast(e?.response?.data?.message || e?.response?.data || e?.message || '1099-MISC publish failed.', 'error');
        } finally { setPublishing(null); }
    };

    const companyCards = [
        { key: 'emp',         title: 'Employee Payroll Journal',              onClick: handleEmployeeJournal,       category: 'payroll' },
        { key: 'con',         title: 'Contractor Payroll Journal',            onClick: handleContractorJournal,     category: 'payroll' },
        { key: 'w2',          title: 'W-2 Preview Report (YTD)',              onClick: handleW2Preview,             category: 'payroll' },
        { key: 'exempt',      title: 'W-2 Exempt Employees Report',           onClick: handleW2Exempt,              category: 'payroll' },
        { key: 'laborcost',   title: 'Labor Cost by Employee (YTD)',          onClick: handleLaborCostReport,       category: 'analytics' },
        { key: 'taxliability',title: 'Tax Liability by Pay Period',           onClick: handleTaxLiabilityReport,   category: 'analytics' },
        { key: '1099',        title: 'Contractor 1099 Summary (YTD)',         onClick: handleContractor1099Summary, category: 'analytics' },
        { key: 'headcount',   title: 'Headcount & Workforce Report',          onClick: handleHeadcountReport,       category: 'analytics' },
        { key: 'benefits',    title: 'Benefits Cost Summary',                 onClick: handleBenefitsCostSummary,   category: 'analytics' },
        { key: 'deductions',  title: 'Deductions Summary',                    onClick: handleDeductionsSummary,     category: 'analytics' },
        { key: 'benefitenroll', title: 'Benefit Enrollment Export (Carrier)', onClick: handleBenefitEnrollmentExport, category: 'carrier' },
        { key: '401kexport',  title: '401k Contribution Export (per Payroll)', onClick: handle401kContributionExport, category: 'carrier' },
        { key: 'hsafsa',      title: 'HSA / FSA Contribution Export',          onClick: handleHsaFsaExport,           category: 'carrier' },
        { key: 'cobra',       title: 'COBRA Eligibility Report',               onClick: handleCobraEligibilityReport, category: 'carrier' },
        { key: 'edi834',      title: 'EDI 834 Enrollment File (X12 5010)',     onClick: handleEdi834Export,           category: 'carrier' },
        { key: 'w2pdf',       title: 'W-2 PDF (IRS Format)',                   onClick: handleW2Pdf,                  category: 'irsforms' },
        { key: 'w2cpdf',      title: 'W-2c Corrected PDF (IRS Format)',        onClick: handleW2cPdf,                 category: 'irsforms' },
        { key: '1099pdf',     title: '1099-NEC PDF (IRS Format)',              onClick: handle1099Pdf,                category: 'irsforms' },
        { key: '941pdf',      title: 'Form 941 Quarterly PDF (IRS Format)',    onClick: handle941Pdf,                 category: 'irsforms' },
    ];

    return (
        <div className={cn('content', styles.reportsContainer)}>
            <div style={{ padding: "16px 24px 16px 16px" }}>
                {/* Year / Quarter selector for IRS PDF forms */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 20, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: '#1B75F0', textTransform: 'uppercase', letterSpacing: 1 }}>IRS Tax Forms (PDF)</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Tax Year:</label>
                        <select value={pdfYear} onChange={e => setPdfYear(Number(e.target.value))}
                            style={{ fontSize: 13, borderRadius: 6, border: '1px solid #ccc', padding: '3px 8px', minWidth: 76 }}>
                            {availablePdfYears.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#555', marginLeft: 8 }}>Quarter (941):</label>
                        <select value={pdfQuarter} onChange={e => setPdfQuarter(Number(e.target.value))}
                            style={{ fontSize: 13, borderRadius: 6, border: '1px solid #ccc', padding: '3px 8px', minWidth: 130 }}>
                            <option value={1}>Q1 (Jan–Mar)</option>
                            <option value={2}>Q2 (Apr–Jun)</option>
                            <option value={3}>Q3 (Jul–Sep)</option>
                            <option value={4}>Q4 (Oct–Dec)</option>
                        </select>
                    </div>
                </div>
                {/* Publish to Portals */}
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '16px 20px', marginTop: 4, marginBottom: 16 }}>
                    <p style={{ fontWeight: 700, fontSize: 13, color: '#0369a1', marginBottom: 6 }}>
                        📤 Publish Tax Docs to Employee &amp; Contractor Portals
                    </p>
                    <p style={{ fontSize: 12, color: '#555', marginBottom: 12 }}>
                        Saves an individual W-2 or 1099-NEC PDF to each person's portal and sends them an email notification.
                        Uses the <strong>Tax Year</strong> selected above.
                    </p>
                    {publishResult && (
                        <div style={{ padding: '8px 14px', borderRadius: 6, background: '#d1fae5', color: '#065f46', fontSize: 13, marginBottom: 12 }}>
                            ✓ Published {publishResult.count} {publishResult.type === 'w2' ? 'W-2(s)' : publishResult.type === 'w2c' ? 'W-2c(s)' : publishResult.type === '1099misc' ? '1099-MISC(s)' : '1099-NEC(s)'} for {publishResult.year}.
                            {publishResult.type !== '1099misc' && ' Recipients have been notified by email and in-app notification.'}
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <button onClick={handlePublishW2s} disabled={!!publishing}
                            style={{ background: publishing === 'w2' ? '#6b7280' : '#1a3a5c', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: publishing ? 'wait' : 'pointer' }}>
                            {publishing === 'w2' ? '⏳ Publishing W-2s…' : 'Publish W-2s'}
                        </button>
                        <button onClick={handlePublishW2cs} disabled={!!publishing}
                            style={{ background: publishing === 'w2c' ? '#6b7280' : '#c0392b', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: publishing ? 'wait' : 'pointer' }}>
                            {publishing === 'w2c' ? '⏳ Publishing W-2c(s)…' : 'Publish Corrected W-2c(s)'}
                        </button>
                        <button onClick={handlePublish1099s} disabled={!!publishing}
                            style={{ background: publishing === '1099' ? '#6b7280' : '#1a3a5c', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: publishing ? 'wait' : 'pointer' }}>
                            {publishing === '1099' ? '⏳ Publishing 1099s…' : 'Publish 1099-NECs'}
                        </button>
                        <button onClick={() => setShowMiscForm(v => !v)} disabled={!!publishing}
                            style={{ background: showMiscForm ? '#6b7280' : '#1B75F0', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                            {showMiscForm ? 'Cancel 1099-MISC' : '+ Issue 1099-MISC'}
                        </button>
                    </div>

                    {/* 1099-MISC manual entry form */}
                    {showMiscForm && (
                        <div style={{ marginTop: 16, background: '#fff', border: '1px solid #fed7aa', borderRadius: 10, padding: 16 }}>
                            <p style={{ fontWeight: 700, fontSize: 13, color: '#c2410c', marginBottom: 8 }}>
                                1099-MISC Recipients — Tax Year {pdfYear}
                            </p>
                            <p style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                                For rents, royalties, prizes, medical payments, attorney fees, and other miscellaneous income. Enter amounts ≥ $600.
                            </p>
                            {miscRecipients.map((r, i) => (
                                <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12, background: '#fafafa' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                        <span style={{ fontWeight: 600, fontSize: 12, color: '#374151' }}>Recipient {i + 1}</span>
                                        {miscRecipients.length > 1 && (
                                            <button onClick={() => setMiscRecipients(p => p.filter((_, idx) => idx !== i))}
                                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>✕ Remove</button>
                                        )}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                                        {[
                                            ['name', 'Full Name *'],
                                            ['businessName', 'Business Name'],
                                            ['taxId', 'TIN / SSN / EIN'],
                                            ['addressLine1', 'Address'],
                                            ['city', 'City'],
                                            ['state', 'State'],
                                            ['zip', 'ZIP'],
                                        ].map(([field, label]) => (
                                            <div key={field}>
                                                <label style={{ fontSize: 11, color: '#6b7280', display: 'block', marginBottom: 2 }}>{label}</label>
                                                <input value={r[field]} onChange={e => updateMiscRecipient(i, field, e.target.value)}
                                                    style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: '5px 8px', fontSize: 12 }} />
                                            </div>
                                        ))}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8, marginTop: 8 }}>
                                        {[
                                            ['box1Rents', 'Box 1 — Rents'],
                                            ['box2Royalties', 'Box 2 — Royalties'],
                                            ['box3OtherIncome', 'Box 3 — Other Income'],
                                            ['box4FederalTaxWithheld', 'Box 4 — Fed. Tax Withheld'],
                                            ['box6MedicalPayments', 'Box 6 — Medical Payments'],
                                            ['box8SubstitutePayments', 'Box 8 — Substitute Payments'],
                                            ['box10CropInsurance', 'Box 10 — Crop Insurance'],
                                            ['box14AttorneyFees', 'Box 14 — Attorney Fees'],
                                        ].map(([field, label]) => (
                                            <div key={field}>
                                                <label style={{ fontSize: 11, color: '#6b7280', display: 'block', marginBottom: 2 }}>{label}</label>
                                                <input type="number" min="0" step="0.01" value={r[field]}
                                                    onChange={e => updateMiscRecipient(i, field, e.target.value)}
                                                    placeholder="0.00"
                                                    style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: '5px 8px', fontSize: 12 }} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 }}>
                                <button onClick={() => setMiscRecipients(p => [...p, { ...emptyMiscRecipient }])}
                                    style={{ fontSize: 12, color: '#1B75F0', background: 'none', border: '1px solid #1B75F0', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontWeight: 600 }}>
                                    + Add Recipient
                                </button>
                                <button onClick={handlePublish1099Misc} disabled={!!publishing}
                                    style={{ background: publishing === '1099misc' ? '#6b7280' : '#1B75F0', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: publishing ? 'wait' : 'pointer' }}>
                                    {publishing === '1099misc' ? '⏳ Generating…' : `Generate & Publish 1099-MISC (${pdfYear})`}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="row g-3 mb-4">
                    {companyCards.filter(c => c.category === 'irsforms').map((item) => (
                        <div className="col-lg-3 col-md-4 col-sm-6" key={item.key}>
                            <div
                                className="border-gray h-100 rounded-4 p-3 py-4 d-flex justify-content-between align-items-center gap-3"
                                onClick={item.onClick}
                                style={{ cursor: downloading ? 'wait' : 'pointer', opacity: downloading && downloading !== item.key ? 0.6 : 1, transition: 'opacity 0.2s', borderLeft: '3px solid #1a3a5c' }}
                            >
                                <div>
                                    <h3 className="company-title fs-6">{item.title}</h3>
                                    <p style={{ fontSize: 11, color: '#666', marginBottom: 0, marginTop: 2 }}>{pdfYear}{item.key === '941pdf' ? ` · Q${pdfQuarter}` : ''}</p>
                                    {downloading === item.key && (
                                        <p style={{ fontSize: 11, color: '#1B75F0', marginBottom: 0, marginTop: 4 }}>Generating PDF...</p>
                                    )}
                                </div>
                                <div style={{ flexShrink: 0 }}>
                                    {downloading === item.key ? <span style={{ fontSize: 18 }}>⏳</span> : <DownloadIcon />}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {['payroll', 'analytics', 'carrier'].map(cat => {
                    const cards = companyCards.filter(c => c.category === cat);
                    return (
                        <div key={cat}>
                            <p style={{ fontWeight: 700, fontSize: 13, color: '#1B75F0', marginTop: 20, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
                                {cat === 'payroll' ? 'Payroll Downloads' : cat === 'analytics' ? 'Analytics Reports' : 'Carrier & Provider Exports'}
                            </p>
                            {cat === 'carrier' && (
                                <p style={{ fontSize: 12, color: '#555', marginBottom: 10 }}>
                                    CSV files formatted for submission to benefits carriers (Aetna, Cigna, etc.) and retirement plan providers (Fidelity, Vanguard, etc.).
                                </p>
                            )}
                            <div className="row g-3">
                                {cards.map((item) => (
                                    <div className="col-lg-3 col-md-4 col-sm-6" key={item.key}>
                            <div
                                className="border-gray h-100 rounded-4 p-3 py-4 d-flex justify-content-between align-items-center gap-3"
                                onClick={item.onClick}
                                style={{ cursor: downloading ? 'wait' : 'pointer', opacity: downloading && downloading !== item.key ? 0.6 : 1, transition: 'opacity 0.2s' }}
                            >
                                <div>
                                    <h3 className="company-title fs-6">{item.title}</h3>
                                    {item.subtitle && !downloading && (
                                        <p style={{ fontSize: 11, color: '#888', marginBottom: 0, marginTop: 2 }}>{item.subtitle}</p>
                                    )}
                                    {downloading === item.key && (
                                        <p style={{ fontSize: 11, color: '#1B75F0', marginBottom: 0, marginTop: 4 }}>Generating...</p>
                                    )}
                                </div>
                                <div style={{ flexShrink: 0 }}>
                                    {downloading === item.key
                                        ? <span style={{ fontSize: 18 }}>⏳</span>
                                        : item.isGenerate
                                        ? <span style={{ fontSize: 20 }}>⚙️</span>
                                        : <DownloadIcon />
                                    }
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                        </div>
                    );
                })}
            </div>
            <div className={cn("mt-4 container-spacing", styles.container)}>
                <div className={cn("p-4", styles.tableContainer)}>
                    <TabList />
                    <Outlet />
                </div>
            </div>
        </div>
    );
};

export default Reports;

